<template>
  <div class="upload">
    <!-- Zona de arrastre: se usa donde adjuntar es la acción principal. El
         resto de diálogos sigue con el campo de una línea de siempre. -->
    <div
      v-if="dropzone"
      class="dropzone"
      :class="{ 'dropzone--over': dragging, 'dropzone--error': invalid }"
      role="button"
      tabindex="0"
      @click="pickFile"
      @keydown.enter.prevent="pickFile"
      @keydown.space.prevent="pickFile"
      @dragover.prevent="dragging = true"
      @dragenter.prevent="dragging = true"
      @dragleave.prevent="dragging = false"
      @drop.prevent="onDrop"
    >
      <v-icon class="dropzone__icon">cloud_upload</v-icon>
      <p class="dropzone__title">
        Arrastra los documentos o haz clic para elegirlos
      </p>
      <p class="dropzone__hint">{{ hint }}</p>
    </div>
    <v-text-field
      v-else
      :label="
        multiple ? 'Seleccione uno o varios adjuntos' : 'Seleccione un Adjunto'
      "
      :value="summary"
      class="upload__field"
      prepend-icon="attach_file"
      readonly
      data-vv-name="attach_file"
      @click="pickFile"
    ></v-text-field>
    <input
      ref="image"
      :multiple="multiple"
      style="display: none"
      type="file"
      data-vv-name="file"
      @change="onFilePicked"
    />

    <!-- Archivos elegidos en esta carga: se pueden quitar antes de guardar. -->
    <ul v-if="picked.length" class="upload__list">
      <li v-for="(file, i) in picked" :key="file.name + i" class="upload__item">
        <v-icon small class="upload__icon">insert_drive_file</v-icon>
        <span class="upload__name" :title="file.name">{{ file.name }}</span>
        <span class="upload__size">{{ humanSize(file.size) }}</span>
        <button
          type="button"
          class="upload__remove"
          title="Quitar este archivo"
          @click="remove(i)"
        >
          <v-icon small>close</v-icon>
        </button>
      </li>
    </ul>

    <img v-if="preview" :src="preview" class="img" height="150" />
  </div>
</template>

<script>
export default {
  props: {
    // Por defecto se mantiene el comportamiento de un solo archivo, para no
    // alterar las pantallas que ya usan este componente.
    multiple: {
      type: Boolean,
      default: false,
    },
    // Presentación como zona de arrastre en vez de campo de una línea.
    dropzone: {
      type: Boolean,
      default: false,
    },
    // Marca el borde en rojo cuando ya se intentó guardar sin adjuntos. El
    // componente no decide la validez: la recibe.
    invalid: {
      type: Boolean,
      default: false,
    },
    hint: {
      type: String,
      default: 'PDF o imagen. Puedes adjuntar varios.',
    },
  },
  data: () => ({
    picked: [],
    preview: '',
    dragging: false,
  }),
  computed: {
    summary() {
      if (!this.picked.length) return ''
      if (this.picked.length === 1) return this.picked[0].name
      return `${this.picked.length} archivos seleccionados`
    },
  },
  methods: {
    pickFile() {
      this.$refs.image.click()
    },
    humanSize(bytes) {
      if (!bytes) return ''
      if (bytes < 1024) return `${bytes} B`
      if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    },
    onDrop(e) {
      this.dragging = false
      this.addFiles(Array.from((e.dataTransfer && e.dataTransfer.files) || []))
    },
    addFiles(list) {
      const files = list.filter(f => f && f.name.lastIndexOf('.') > 0)
      if (!files.length) return
      this.picked = this.multiple
        ? this.picked.concat(files)
        : files.slice(0, 1)
      this.updatePreview()
      this.emit()
    },
    onFilePicked(e) {
      const files = Array.from(e.target.files || []).filter(
        f => f && f.name.lastIndexOf('.') > 0
      )
      // Al elegir de nuevo se acumulan, para poder cargar de tandas distintas.
      this.picked = this.multiple
        ? this.picked.concat(files)
        : files.slice(0, 1)
      // El input se limpia para que volver a elegir el mismo archivo dispare
      // el evento change otra vez.
      e.target.value = ''
      this.updatePreview()
      this.emit()
    },
    remove(index) {
      this.picked.splice(index, 1)
      this.updatePreview()
      this.emit()
    },
    updatePreview() {
      const image = this.picked.find(f => f.type && f.type.match('image.*'))
      if (!image) {
        this.preview = ''
        return
      }
      const fr = new FileReader()
      fr.readAsDataURL(image)
      fr.addEventListener('load', () => {
        this.preview = fr.result || ''
      })
    },
    emit() {
      // Compatibilidad: en modo simple se emite el archivo (o '' si no hay),
      // igual que antes; en modo múltiple, el arreglo completo.
      if (this.multiple) {
        this.$emit('clicked', this.picked.slice())
        return
      }
      this.$emit('clicked', this.picked.length ? this.picked[0] : '')
    },
  },
}
</script>

<style scoped>
/* Zona de arrastre: adjuntar es la acción central del rechazo, así que ocupa
   área en vez de esconderse en un campo de una línea. */
.dropzone {
  border: 1px dashed #cbd5e1;
  border-radius: 10px;
  background: #f8fafc;
  padding: 18px 16px;
  text-align: center;
  cursor: pointer;
  transition: border-color 0.18s ease, background 0.18s ease;
}
.dropzone:hover,
.dropzone:focus-visible {
  border-color: #254f87;
  background: #f1f5f9;
  outline: none;
}
.dropzone:focus-visible {
  box-shadow: 0 0 0 3px rgba(37, 79, 135, 0.18);
}
.dropzone--over {
  border-color: #254f87;
  border-style: solid;
  background: #eef2f7;
}
.dropzone--error {
  border-color: #fca5a5;
  background: #fef2f2;
}
.dropzone__icon {
  color: #94a3b8 !important;
  font-size: 26px !important;
}
.dropzone--over .dropzone__icon,
.dropzone:hover .dropzone__icon {
  color: #254f87 !important;
}
.dropzone__title {
  margin: 6px 0 2px;
  font-size: 13px;
  font-weight: 600;
  color: #334155;
}
.dropzone__hint {
  margin: 0;
  font-size: 12px;
  color: #94a3b8;
}

.upload__list {
  list-style: none;
  padding: 0;
  margin: 4px 0 0;
}
.upload__item {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  background: #f8fafc;
  margin-bottom: 4px;
  font-size: 13px;
}
.upload__icon {
  color: #64748b !important;
}
.upload__name {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #0f172a;
}
.upload__size {
  color: #94a3b8;
  font-size: 12px;
}
.upload__remove {
  border: none;
  background: transparent;
  cursor: pointer;
  line-height: 1;
  padding: 2px;
  border-radius: 4px;
}
.upload__remove:hover {
  background: #fee2e2;
}
.upload__remove .v-icon {
  color: #b91c1c !important;
}
.img {
  max-width: 100%;
}
</style>
